/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: purchase_order_line_items entity configuration
 * Generator: scripts/generate-api-clients/generate-api-clients.ts
 *
 * This file contains the API client for the purchase_order_line_items table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the purchase_order_line_items API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, purchase_order_id, material_id, description, quantity, unit_cost, total_cost, status, received_quantity, job_id, lot_id, area_id, job_allocations, lot_allocations, created_at, updated_at, status_id, trig_status_name, trig_status_code, trig_status_color, notes, trig_lot_number, trig_lot_address, trig_lot_square_footage, trig_lot_phase_name, trig_area_name, trig_area_sqft, trig_area_type_code, delivery_date, trig_material_color, redline_amount, trig_material_name, version, trig_material_unit_of_measure, slab_length, slab_width, slab_thickness, slab_sqft, cost_per_sqft, trig_material_department, trig_material_code, trig_material_manufacturer, pulled_at, pulled_by, finish, trig_material_vendor_id, trig_material_vendor_name, trig_material_vendor_sku, department, block_numbers, line_number, source_area_field_junction_id]
 * Searchable fields: []
 * Filterable fields: [id, purchase_order_id, material_id, job_id, lot_id, area_id, status_id, trig_material_vendor_id, trig_material_vendor_sku, line_number, source_area_field_junction_id]
 */

import { httpClient } from '../http-client.js';
import { globalSearchConfig } from '../../../config/search/global-search-config.js';
import { matchesAllTerms } from '../../../config/search/search-utils.js';
import type {
  PurchaseOrderLineItemsRow,
  PurchaseOrderLineItemsInsert,
  PurchaseOrderLineItemsUpdate
} from '../generated_schemas/generated-purchase_order_line_items-schema.js';
import { PurchaseOrderLineItemsQueryOptions } from '../generated_schemas/generated-purchase_order_line_items-schema.js';
export type { PurchaseOrderLineItemsQueryOptions };

// Internal response type with non-null data (not exported to avoid conflicts with schema's Response type)
interface PurchaseOrderLineItemsClientResponse {
  data: PurchaseOrderLineItemsRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for purchase_order_line_items table operations
 *
 * @description Provides type-safe access to purchase_order_line_items API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class PurchaseOrderLineItemsClient {
  private readonly basePath = '/purchase-order-line-items';

  /**
   * Get all purchase_order_line_items records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of purchase_order_line_items records
   */
  async getAll(options?: PurchaseOrderLineItemsQueryOptions): Promise<PurchaseOrderLineItemsRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<PurchaseOrderLineItemsClientResponse>(url);
    return response.data;
  }

  /**
   * Get a single purchase_order_line_items record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the purchase_order_line_items record
   */
  async getById(id: string): Promise<PurchaseOrderLineItemsRow> {
    const response = await httpClient.get<{
      data: PurchaseOrderLineItemsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new purchase_order_line_items record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created purchase_order_line_items record
   */
  async create(data: PurchaseOrderLineItemsInsert): Promise<PurchaseOrderLineItemsRow> {
    const response = await httpClient.post<{
      data: PurchaseOrderLineItemsRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing purchase_order_line_items record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated purchase_order_line_items record
   */
  async update(id: string, data: PurchaseOrderLineItemsUpdate): Promise<PurchaseOrderLineItemsRow> {
    const response = await httpClient.put<{
      data: PurchaseOrderLineItemsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a purchase_order_line_items record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple purchase_order_line_items records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created purchase_order_line_items records
   */
  async createMany(data: PurchaseOrderLineItemsInsert[]): Promise<PurchaseOrderLineItemsRow[]> {
    const response = await httpClient.post<{
      data: PurchaseOrderLineItemsRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple purchase_order_line_items records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search purchase_order_line_items with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    query: string,
    columns?: string[],
    options?: PurchaseOrderLineItemsQueryOptions
  ): Promise<PurchaseOrderLineItemsClientResponse> {
    throw new Error('Search operations are not available for purchase_order_line_items - no searchable fields configured');
  }

  /**
   * Get count of purchase_order_line_items records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a purchase_order_line_items record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'purchase_order_line_items',
      publicFields: ['id', 'purchase_order_id', 'material_id', 'description', 'quantity', 'unit_cost', 'total_cost', 'status', 'received_quantity', 'job_id', 'lot_id', 'area_id', 'job_allocations', 'lot_allocations', 'created_at', 'updated_at', 'status_id', 'trig_status_name', 'trig_status_code', 'trig_status_color', 'notes', 'trig_lot_number', 'trig_lot_address', 'trig_lot_square_footage', 'trig_lot_phase_name', 'trig_area_name', 'trig_area_sqft', 'trig_area_type_code', 'delivery_date', 'trig_material_color', 'redline_amount', 'trig_material_name', 'version', 'trig_material_unit_of_measure', 'slab_length', 'slab_width', 'slab_thickness', 'slab_sqft', 'cost_per_sqft', 'trig_material_department', 'trig_material_code', 'trig_material_manufacturer', 'pulled_at', 'pulled_by', 'finish', 'trig_material_vendor_id', 'trig_material_vendor_name', 'trig_material_vendor_sku', 'department', 'block_numbers', 'line_number', 'source_area_field_junction_id'],
      searchableFields: [],
      filterableFields: ['id', 'purchase_order_id', 'material_id', 'job_id', 'lot_id', 'area_id', 'status_id', 'trig_material_vendor_id', 'trig_material_vendor_sku', 'line_number', 'source_area_field_junction_id'],
      sortableFields: ['created_at', 'updated_at', 'line_number'],
      readonlyFields: ['id', 'created_at', 'updated_at', 'trig_status_name', 'trig_status_code', 'trig_status_color', 'trig_lot_number', 'trig_lot_address', 'trig_lot_square_footage', 'trig_lot_phase_name', 'trig_area_name', 'trig_area_sqft', 'trig_area_type_code', 'trig_material_color', 'trig_material_name', 'trig_material_unit_of_measure', 'trig_material_department', 'trig_material_code', 'trig_material_manufacturer', 'trig_material_vendor_id', 'trig_material_vendor_name', 'trig_material_vendor_sku'],
      requiredFields: ['purchase_order_id', 'material_id', 'quantity', 'unit_cost', 'total_cost'],
    };
  }
}

// Export singleton instance for convenience
export const purchaseOrderLineItemsClient = new PurchaseOrderLineItemsClient();
